import ApplicationList from './ApplicationList';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Event } from '../types';
import { Calendar, MapPin, Tag, ArrowLeft, Send, Shield, AlertCircle, Star, User as UserIcon, AlertTriangle, Flag, CreditCard as Edit, Share2, Users, Clock, TrendingUp, Heart, Sparkles, X, FileText, Expand, UserMinus, Check, MoreVertical, Trash2, XCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { getApplicationsForEvent, getUserApplicationForEvent } from '../services/eventService';
import { getUserById, toggleFavorite as toggleFavoriteService } from '../services/userService';
import { getEventImage } from '../services/eventImageService';
import { getApproximateLocation, isAcceptedMember } from '../utils/locationDisplay';
import VerificationRedirect from './VerificationRedirect';
import ReportForm from './ReportForm';
import EditEventModal from './EditEventModal';
import SubscriptionModal from './SubscriptionModal';
import VybeCounterModal from './VybeCounterModal';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { useOnboarding } from '../onboarding/OnboardingCoach';
import { buildGates } from '../utils/gates';
import { motion, AnimatePresence } from 'framer-motion';
import { canApplyToVybe, consumeVybeApplyLimitWithRefresh, refundVybeApplyTokenWithRefresh } from '../utils/vybeLimits';
import { trackVybeApplicationSent, standardizeCategory } from '../utils/ga4';
import { trackEvent, trackPageView } from '../services/analyticsService';
import { trackVybeView, trackJoinVybe, trackLeaveVybe, trackVybeShared, trackVybeCancelled } from '../services/trackingService';
import { trackJoinVybeClicked, trackJoinVybeResult } from '../services/behaviorTracking';
import TokenTopUpModal from './TokenTopUpModal';
import ConfirmCounterBadge from './vybe/ConfirmCounterBadge';
import PostEventRatingPrompt from './vybe/PostEventRatingPrompt';
import SocialShareBar from './vybe/SocialShareBar';
import ApplicationConfirmationModal from './ApplicationConfirmationModal';
import WhosGoingList from './vybe/WhosGoingList';
import PushNotificationPrompt from './PushNotificationPrompt';
import VybeAcceptedCard from './VybeAcceptedCard';
import VybeStatusCard from './VybeStatusCard';
import AddToCalendar from './AddToCalendar';
import MapView from './MapView';
import MapModal from './MapModal';
import ShareModal from './ShareModal';

interface EventDetailProps {
  event: Event;
  onBack: () => void;
  onApplied: () => void;
}

const EventDetail: React.FC<EventDetailProps> = ({ event, onBack, onApplied }) => {
  const { currentUser, isAuthenticated, refreshCurrentUser } = useAuth();
  const navigate = useNavigate();
  const { ensure } = useOnboarding();

  if (typeof buildGates !== 'function') {
    console.error('[EventDetail] buildGates missing — check import path.');
    return <div style={{padding:16, color: 'white'}}>Something went wrong loading feature gates.</div>;
  }

  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showReportForm, setShowReportForm] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showVybeCounterModal, setShowVybeCounterModal] = useState(false);
  const [applications, setApplications] = useState<any[]>([]);
  const [poster, setPoster] = useState<any>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [userApplication, setUserApplication] = useState<any>(null);
  const [confirmedCount, setConfirmedCount] = useState(0);
  const [totalAccepted, setTotalAccepted] = useState(0);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [ratingTargets, setRatingTargets] = useState<any[]>([]);
  const [showApplicationConfirmation, setShowApplicationConfirmation] = useState(false);
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [limitErrorMessage, setLimitErrorMessage] = useState<string>('');
  const [showMapModal, setShowMapModal] = useState(false);
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  const [showAdminMenu, setShowAdminMenu] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showPushPrompt, setShowPushPrompt] = useState(false);

  const isOwner = !!currentUser?.id && currentUser.id === event.posterId;
  const isAdmin = !!currentUser?.isAdmin;
  const canEdit = isOwner || isAdmin;
  const isAcceptedAttendee = userApplication?.status === 'accepted';
  const canAddToCalendar = isOwner || isAcceptedAttendee;

  const scrollToJoin = () => {
    const joinSection = document.getElementById('join-section');
    if (joinSection) {
      joinSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  useEffect(() => {
    const loadEventData = async () => {
      // Track page view for vybe detail (once per session)
      trackPageView('vybe_detail', {
        vybe_id: event.id,
        category: event.category,
        user_id: currentUser?.id
      }, currentUser?.id);

      // Track vybe view for B2B analytics
      trackVybeView(event.id, currentUser?.id);

      try {
        // Parallelize independent queries to improve performance
        const queries = [];

        // Query 1: Fetch poster data
        if (event.posterId) {
          queries.push(getUserById(event.posterId).then(userData => ({ type: 'poster', data: userData })));
        }

        // Query 2: Fetch applications if owner
        if (isOwner) {
          queries.push(getApplicationsForEvent(event.id).then(apps => ({ type: 'applications', data: apps })));
        }

        // Query 3: Fetch user's application if not owner
        if (currentUser?.id && !isOwner) {
          queries.push(getUserApplicationForEvent(event.id, currentUser.id).then(app => ({ type: 'userApplication', data: app })));
        }

        // Query 4: Fetch accepted participants count
        queries.push(
          supabase
            .from('event_applications')
            .select('applicant_id')
            .eq('event_id', event.id)
            .eq('status', 'accepted')
            .then(({ data }) => ({ type: 'participants', data }))
        );

        // Execute all queries in parallel
        const results = await Promise.all(queries);

        // Process results
        for (const result of results) {
          if (result.type === 'poster' && result.data) {
            setPoster(result.data);
          } else if (result.type === 'applications') {
            setApplications(result.data || []);
          } else if (result.type === 'userApplication') {
            setUserApplication(result.data);
          } else if (result.type === 'participants' && result.data) {
            setTotalAccepted(result.data.length);
            setConfirmedCount(result.data.length);
          }
        }

        // Update favorites state
        if (currentUser && currentUser.favorites) {
          setIsFavorite(currentUser.favorites.includes(event.id));
        }

        // Check if event has passed and prompt for ratings (depends on userApplication, so run after)
        if (currentUser?.id && userApplication?.status === 'accepted') {
          const eventDate = new Date(event.date);
          const now = new Date();
          const hasEventPassed = eventDate < now;

          if (hasEventPassed) {
            // Check localStorage to see if we've shown the prompt this session
            const promptKey = `rating_prompt_shown_${event.id}_${currentUser.id}`;
            const promptShown = localStorage.getItem(promptKey);

            if (!promptShown) {
              // Parallelize rating-related queries
              const [existingRatingsResult, otherParticipantsResult] = await Promise.all([
                supabase
                  .from('event_ratings')
                  .select('id')
                  .eq('event_id', event.id)
                  .eq('rater_id', currentUser.id)
                  .limit(1),
                supabase
                  .from('event_applications')
                  .select('applicant_id, profiles:applicant_id(id, full_name, username, avatar_url)')
                  .eq('event_id', event.id)
                  .eq('status', 'accepted')
                  .neq('applicant_id', currentUser.id)
              ]);

              const existingRatings = existingRatingsResult.data;
              const otherParticipants = otherParticipantsResult.data;

              if (!existingRatings?.length && otherParticipants && otherParticipants.length > 0) {
                const targets = otherParticipants.map((p: any) => ({
                  userId: p.applicant_id,
                  name: p.profiles?.username || 'Member',
                  avatarUrl: p.profiles?.avatar_url
                }));
                setRatingTargets(targets);
                setShowRatingModal(true);
                localStorage.setItem(promptKey, 'true');
              }
            }
          }
        }
      } catch (error) {
        console.error('Error loading event data:', error);
      }
    };

    loadEventData();
  }, [event, isOwner, currentUser, userApplication]);

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrollPosition = window.scrollY + window.innerHeight;
          const documentHeight = document.documentElement.scrollHeight;
          const distanceFromBottom = documentHeight - scrollPosition;

          setShowStickyBar(window.scrollY > 600 && distanceFromBottom > 200);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const distance = useMemo((): number | null => {
    if (
      !currentUser?.latitude ||
      !currentUser?.longitude ||
      !event.latitude ||
      !event.longitude
    ) {
      return null;
    }
    const R = 3958.8; // miles
    const dLat = (event.latitude - currentUser.latitude) * Math.PI / 180;
    const dLon = (event.longitude - currentUser.longitude) * Math.PI / 180;
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(currentUser.latitude * Math.PI / 180) *
        Math.cos(event.latitude * Math.PI / 180) *
        Math.sin(dLon / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }, [currentUser?.latitude, currentUser?.longitude, event.latitude, event.longitude]);

  const renderStarRating = (rating: number | undefined, count?: number) => {
    if (!count || count < 3) {
      return (
        <div className="inline-flex items-center bg-blue-500/20 border border-blue-500/50 text-blue-400 px-2 py-1 rounded-full text-xs font-bold">
          NEW
        </div>
      );
    }

    if (!rating) return null;

    return (
      <div className="flex items-center gap-1">
        <div className="flex">
          {[...Array(5)].map((_, i) => (
            <Star
              key={i}
              size={14}
              className={i < Math.floor(rating) ? 'fill-yellow-400 text-yellow-400' : 'text-theme-text-secondary/40'}
            />
          ))}
        </div>
        <span className="ml-1 text-sm font-semibold text-theme-text-main">{rating.toFixed(1)}</span>
        {count !== undefined && <span className="text-xs text-theme-text-secondary">({count})</span>}
      </div>
    );
  };

  const handleApply = async () => {
    if (!currentUser) return;

    if (false && import.meta.env.VITE_ONBOARDING_JOURNEY === '1') {
      const onboardingCompleted = await ensure(['photo', 'interests']);
      if (!onboardingCompleted) {
        setError('Finish your profile to apply to Vybes.');
        return;
      }
    }

    // Verification is optional - no blocking checks needed

    // Check application limits using new token system
    const isSubscribed = currentUser.subscriptionTier === 'subscribed';
    const isBotVybe = event.isBotCreated || event.systemHost || event.autoAcceptEnabled;

    setIsSubmitting(true);
    setError('');

    try {
      // Track application start
      trackEvent('vybe_apply_started', {
        event_id: event.id,
        category: event.category,
        user_id: currentUser.id
      }, currentUser.id);

      // Bot-created Vybes skip token consumption
      if (!event.isBotCreated && !event.systemHost) {
        // Generate idempotency key for this operation to prevent double-consumption on retry
        const idempotencyKey = `${currentUser.id}_apply_${event.id}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        // Atomically check limits and consume token BEFORE creating application
        // Validation (message not empty) has already occurred above
        // This wrapper automatically refreshes user state on success
        const consumeResult = await consumeVybeApplyLimitWithRefresh(
          currentUser.id,
          isSubscribed,
          refreshCurrentUser,
          idempotencyKey
        );

        if (!consumeResult.success) {
          setLimitErrorMessage(consumeResult.reason || 'Unable to apply to Vybe at this time');
          setShowTokenModal(true);
          trackJoinVybeResult(event.id, event.title, 'blocked_no_tokens', consumeResult.reason);
          setIsSubmitting(false);
          return;
        }
      }

      const hostId = event.posterId;
      if (!hostId) throw new Error('This Vybe has no host assigned.');

      // Bot vybes auto-accept, human vybes start as pending
      const applicationStatus = isBotVybe ? 'accepted' : 'pending';

      // Check if there's an existing application (including withdrawn)
      const { data: existingApp } = await supabase
        .from('event_applications')
        .select('id, status')
        .eq('event_id', event.id)
        .eq('applicant_id', currentUser.id)
        .maybeSingle();

      let appError = null;

      if (existingApp) {
        // If application exists and is not withdrawn, show error
        if (existingApp.status !== 'withdrawn') {
          setError("You've already applied to this Vybe.");
          trackJoinVybeResult(event.id, event.title, 'blocked_already_applied');
          if (!event.isBotCreated && !event.systemHost) {
            await refundVybeApplyTokenWithRefresh(currentUser.id, refreshCurrentUser);
          }
          setIsSubmitting(false);
          return;
        }

        // Update the withdrawn application to reapply
        const { error: updateErr } = await supabase
          .from('event_applications')
          .update({
            status: applicationStatus,
            message: message.trim() || 'Instant join (bot vybe)',
            created_at: new Date().toISOString(), // Reset application time
          })
          .eq('id', existingApp.id);

        appError = updateErr;
      } else {
        // Insert new application
        const { error: insErr } = await supabase
          .from('event_applications')
          .insert([{
            event_id: event.id,
            applicant_id: currentUser.id,
            host_id: hostId,
            message: message.trim() || 'Instant join (bot vybe)',
            status: applicationStatus,
          }]);

        appError = insErr;
      }

      if (appError) {
        const code = (appError as any)?.code || '';
        const msg = (appError as any)?.message || '';

        if (code === '42501' || /row-level security/i.test(msg) || /permission/i.test(msg)) {
          setError("You don't have permission to apply. Please log in again and retry.");
        } else {
          setError(msg || 'Something didn\'t work — try again in a moment.');
        }
        // Refund the token since application failed (only for non-bot vybes)
        if (!event.isBotCreated && !event.systemHost) {
          await refundVybeApplyTokenWithRefresh(currentUser.id, refreshCurrentUser);
        }
        setIsSubmitting(false);
        return;
      }

      // Notify the organizer about the new application (skip for bot vybes)
      if (!event.isBotCreated && !event.systemHost) {
        try {
          await supabase.from('notifications').insert({
            user_id: hostId,
            type: 'new_application',
            content: `${currentUser.username || 'Someone'} wants to join your Vybe "${event.title}"`,
            related_id: event.id,
            metadata: { applicant_id: currentUser.id, event_id: event.id },
            created_at: new Date().toISOString(),
          });
        } catch (notifErr) {
          console.error('Failed to send notification to organizer:', notifErr);
        }
      }

      // For bot vybes that are auto-accepted, trigger acceptance flow
      if (isBotVybe) {
        try {
          // Call edge function to handle acceptance flow (emails, thread, system message)
          const { data: { session } } = await supabase.auth.getSession();

          const response = await fetch(
            `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/on-application-accepted`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session?.access_token || import.meta.env.VITE_SUPABASE_ANON_KEY}`,
              },
              body: JSON.stringify({
                eventId: event.id,
                applicantId: currentUser.id,
              }),
            }
          );

          if (!response.ok) {
            const errorText = await response.text();
            console.error('Error calling on-application-accepted function:', errorText);
          } else {
            console.log('Successfully triggered acceptance flow for bot vybe');
          }
        } catch (funcError) {
          console.error('Error calling acceptance edge function:', funcError);
          // Don't fail the whole operation if the edge function fails
        }
      } else {
        // Notify the applicant that their request was sent
        try {
          await supabase.from('notifications').insert({
            user_id: currentUser.id,
            type: 'application_sent',
            content: `Your request to join "${event.title}" has been sent!`,
            related_id: event.id,
            metadata: { event_id: event.id },
            created_at: new Date().toISOString(),
          });
        } catch (notifErr) {
          console.error('Failed to send confirmation notification:', notifErr);
        }
      }

      // Track application sent to GA4
      trackVybeApplicationSent({
        vybe_id: event.id,
        user_id: currentUser.id,
        category: standardizeCategory(event.category),
        host_id: hostId,
        apply_tokens_remaining: 999, // Applying is now unlimited (no tokens required)
      });

      // Track successful application
      trackEvent('vybe_apply_completed', {
        event_id: event.id,
        category: event.category,
        user_id: currentUser.id,
        auto_accepted: isBotVybe
      }, currentUser.id);

      // Track join_vybe for B2B analytics
      trackJoinVybe(event.id, currentUser.id);

      // Track successful join in behavior tracking
      trackJoinVybeResult(event.id, event.title, 'success');

      setSuccess(true);

      // Show push notification prompt after meaningful action
      if (!isBotVybe) {
        setShowPushPrompt(true);
      }
      setMessage('');
      setShowApplicationConfirmation(true);
      // Refresh user application status
      const application = await getUserApplicationForEvent(event.id, currentUser.id);
      setUserApplication(application);
      onApplied();
    } catch (e: any) {
      // Track application failure
      trackEvent('vybe_apply_failed', {
        event_id: event.id,
        category: event.category,
        user_id: currentUser.id,
        error: e?.message || 'Unknown error'
      }, currentUser.id);

      // Track blocked result in behavior tracking
      trackJoinVybeResult(event.id, event.title, 'blocked_error', e?.message);

      setError(e?.message || 'Something went wrong.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWithdraw = async () => {
    if (!currentUser || !userApplication) return;

    setIsWithdrawing(true);
    setError('');

    try {
      const { error: withdrawError } = await supabase
        .from('event_applications')
        .update({ status: 'withdrawn' })
        .eq('id', userApplication.id);

      if (withdrawError) throw withdrawError;

      const isBotVybe = event.isBotCreated || event.systemHost || event.autoAcceptEnabled;

      if (isBotVybe) {
        const { data: remainingApps } = await supabase
          .from('event_applications')
          .select('applicant_id')
          .eq('event_id', event.id)
          .eq('status', 'accepted');

        const remainingCount = remainingApps?.length || 0;

        const eventDate = new Date(event.date);
        const hoursUntilEvent = (eventDate.getTime() - Date.now()) / (1000 * 60 * 60);

        if (remainingCount === 0) {
          await supabase
            .from('events')
            .update({ status: 'cancelled' })
            .eq('id', event.id);
        } else if (remainingCount === 1 && hoursUntilEvent <= 24) {
          const lastUser = remainingApps?.[0];
          if (lastUser) {
            await supabase.from('notifications').insert({
              user_id: lastUser.applicant_id,
              type: 'event_reminder',
              content: 'This Vybe may not go ahead unless more people join.',
              related_id: event.id,
            });
          }
        }
      } else {
        await supabase.from('notifications').insert({
          user_id: event.posterId,
          type: 'application_update',
          content: `${currentUser.username || 'Someone'} has withdrawn from your Vybe "${event.title}"`,
          related_id: event.id,
        });
      }

      // Track leave_vybe for B2B analytics
      trackLeaveVybe(event.id, currentUser.id, 'user_withdrawn');

      // Update local state to reflect withdrawal
      setUserApplication({ ...userApplication, status: 'withdrawn' });
      onApplied();
    } catch (e: any) {
      setError(e?.message || 'Failed to withdraw from Vybe');
    } finally {
      setIsWithdrawing(false);
    }
  };

  const handleEditEvent = () => {
    setShowEditModal(true);
    setShowAdminMenu(false);
  };

  const handleCancelEvent = async () => {
    if (!confirm('Are you sure you want to cancel this Vybe? This will notify all accepted members.')) {
      return;
    }

    try {
      console.log('[EventDetail] Cancelling Vybe:', {
        eventId: event.id,
        userId: currentUser?.id,
        isAdmin,
        isOwner
      });

      // If admin is cancelling someone else's vybe, use the admin function
      if (isAdmin && !isOwner) {
        const { data, error } = await supabase.rpc('admin_cancel_vybe', {
          p_event_id: event.id
        });

        console.log('[EventDetail] Admin cancel result:', { data, error });

        if (error) {
          console.error('[EventDetail] Admin cancel failed:', error);
          throw error;
        }
      } else {
        // Owner can cancel directly
        const { data, error } = await supabase
          .from('events')
          .update({ status: 'cancelled' })
          .eq('id', event.id)
          .select();

        console.log('[EventDetail] Cancel result:', { data, error });

        if (error) {
          console.error('[EventDetail] Cancel failed:', error);
          throw error;
        }
      }

      // Track vybe_cancelled for B2B analytics
      if (currentUser?.id) {
        trackVybeCancelled(event.id, currentUser.id, 'host_cancelled');
      }

      onApplied();
      onBack();
    } catch (e: any) {
      console.error('[EventDetail] Cancel error:', e);
      setError(e?.message || 'Failed to cancel Vybe');
      alert(`Failed to cancel Vybe: ${e?.message || 'Unknown error'}\n\nCheck console for details.`);
    }
    setShowAdminMenu(false);
  };

  const handleDeleteEvent = async () => {
    if (!confirm('Are you sure you want to permanently DELETE this Vybe? This cannot be undone and will remove all applications.')) {
      return;
    }

    setIsDeleting(true);
    try {
      const { error } = await supabase
        .from('events')
        .delete()
        .eq('id', event.id);

      if (error) throw error;

      onApplied();
      onBack();
    } catch (e: any) {
      setError(e?.message || 'Failed to delete Vybe');
    } finally {
      setIsDeleting(false);
    }
    setShowAdminMenu(false);
  };

  const handleShare = () => {
    setShowShareModal(true);
  };

  const toggleFavorite = async () => {
    if (!currentUser) return;

    const newState = !isFavorite;
    setIsFavorite(newState);

    const success = await toggleFavoriteService(currentUser.id, event.id);
    if (!success) {
      setIsFavorite(!newState);
      console.error('Failed to update favorite');
    } else {
      // Refresh user data to get updated favorites list
      await refreshCurrentUser();
    }
  };

  const spotsRemaining = event.maxApplicants ? event.maxApplicants - (event.currentApplicants || 0) : null;
  const isAlmostFull = spotsRemaining !== null && spotsRemaining > 0 && spotsRemaining <= 3;
  const isFull = spotsRemaining !== null && spotsRemaining <= 0;

  return (
    <div className="min-h-screen bg-theme-background pb-20">
      {/* Hero Section */}
      <div className="relative h-[300px] sm:h-[450px] overflow-hidden">
        {event.imageGenerationStatus === 'generating' || event.imageGenerationStatus === 'pending' ? (
          <div className="w-full h-full bg-gradient-to-br from-primary-200 to-primary-300 flex items-center justify-center">
            <div className="text-center">
              <div className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-red-500 to-pink-600 rounded-full shadow-lg">
                <span className="text-base font-semibold text-white">Image on it's way!</span>
              </div>
            </div>
          </div>
        ) : (
          <>
            <motion.img
              initial={{ scale: 1.05 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5 }}
              src={getEventImage(event)}
              alt={event.title}
              className="w-full h-full object-cover"
              loading="eager"
              decoding="async"
            />
            {event.unsplashPhotographerName && event.unsplashPhotographerUsername && (
              <div className="absolute bottom-4 left-4 z-10">
                <a
                  href={`https://unsplash.com/@${event.unsplashPhotographerUsername}?utm_source=vybe_tribe&utm_medium=referral`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-white/90 bg-black/50 backdrop-blur-sm px-3 py-1.5 rounded-full hover:bg-black/70 transition-colors inline-block"
                >
                  Photo by {event.unsplashPhotographerName} on Unsplash
                </a>
              </div>
            )}
          </>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-theme-background via-theme-background/60 to-transparent"></div>

        {/* Top Actions */}
        <div className="absolute top-0 left-0 right-0 p-2 sm:p-4 flex justify-between items-center z-10 gap-2">
          <button
            onClick={onBack}
            className="flex items-center justify-center text-white bg-theme-cards/80 backdrop-blur-sm hover:bg-theme-cards transition-all px-3 sm:px-4 py-2 rounded-full font-medium shadow-lg border border-theme-cards text-sm min-h-[44px]"
          >
            <ArrowLeft size={18} className="sm:mr-2 flex-shrink-0" />
            <span className="hidden sm:inline">Back</span>
          </button>

          <div className="flex gap-1 sm:gap-2 flex-wrap justify-end">
            {currentUser && (
              <button
                onClick={toggleFavorite}
                className="flex items-center justify-center text-white bg-theme-cards/80 backdrop-blur-sm hover:bg-theme-cards transition-all w-10 h-10 rounded-full shadow-lg border border-theme-cards"
                title="Save to favorites"
              >
                <Heart size={18} className={isFavorite ? 'fill-theme-primary text-theme-primary' : ''} />
              </button>
            )}

            <button
              onClick={handleShare}
              className="flex items-center text-white bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 transition-all px-3 sm:px-4 py-2 rounded-full shadow-lg font-semibold text-sm min-h-[44px]"
              title="Share this Vybe"
            >
              <Share2 size={16} className="sm:mr-2" />
              <span className="hidden sm:inline">Share</span>
            </button>

            {canAddToCalendar && (
              <div className="flex items-center">
                <AddToCalendar event={event} compact={true} />
              </div>
            )}

            {canEdit && (
              <>
                {isAdmin ? (
                  <div className="relative">
                    <button
                      onClick={() => setShowAdminMenu(!showAdminMenu)}
                      className="flex items-center text-white bg-theme-cards/80 backdrop-blur-sm hover:bg-theme-cards transition-all px-3 sm:px-4 py-2 rounded-full shadow-lg border border-theme-cards text-sm min-h-[44px]"
                      title="Admin Actions"
                    >
                      <Shield size={16} className="sm:mr-2 text-accent2" />
                      <span className="hidden sm:inline">Admin</span>
                      <MoreVertical size={14} className="ml-1" />
                    </button>

                    {showAdminMenu && (
                      <>
                        <div
                          className="fixed inset-0 z-40"
                          onClick={() => setShowAdminMenu(false)}
                        />
                        <div className="absolute right-0 mt-2 w-48 bg-theme-cards border border-theme-cards rounded-lg shadow-xl z-50 overflow-hidden">
                          <button
                            onClick={handleEditEvent}
                            className="w-full flex items-center px-4 py-3 text-white hover:bg-primary-100/30 transition-colors text-left text-sm"
                          >
                            <Edit size={16} className="mr-3" />
                            Edit Vybe
                          </button>
                          <button
                            onClick={handleCancelEvent}
                            className="w-full flex items-center px-4 py-3 text-orange-400 hover:bg-primary-100/30 transition-colors text-left text-sm"
                          >
                            <XCircle size={16} className="mr-3" />
                            Cancel Vybe
                          </button>
                          <button
                            onClick={handleDeleteEvent}
                            disabled={isDeleting}
                            className="w-full flex items-center px-4 py-3 text-red-400 hover:bg-primary-100/30 transition-colors text-left text-sm disabled:opacity-50"
                          >
                            <Trash2 size={16} className="mr-3" />
                            {isDeleting ? 'Deleting...' : 'Delete Vybe'}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={handleEditEvent}
                    className="flex items-center text-white bg-theme-cards/80 backdrop-blur-sm hover:bg-theme-cards transition-all px-3 sm:px-4 py-2 rounded-full shadow-lg border border-theme-cards text-sm min-h-[44px]"
                    title="Edit Vybe"
                  >
                    <Edit size={16} className="sm:mr-2" />
                    <span className="hidden sm:inline">Edit</span>
                  </button>
                )}
              </>
            )}

            {!isOwner && isAuthenticated && (
              <>
                {userApplication?.status === 'accepted' ? (
                  <button
                    onClick={handleWithdraw}
                    disabled={isWithdrawing}
                    className="flex items-center justify-center text-white bg-theme-cards/80 backdrop-blur-sm hover:bg-theme-cards transition-all w-10 h-10 sm:w-auto sm:px-4 sm:py-2 rounded-full shadow-lg border border-theme-cards text-sm min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Withdraw from Vybe"
                  >
                    <UserMinus size={16} className="sm:mr-2" />
                    <span className="hidden sm:inline">{isWithdrawing ? 'Withdrawing...' : 'Withdraw'}</span>
                  </button>
                ) : (!userApplication || userApplication.status === 'withdrawn') && (
                  <button
                    onClick={() => {
                      trackJoinVybeClicked(event.id, event.title, 'header_button', event.isBotCreated || event.systemHost || event.autoAcceptEnabled || false);
                      scrollToJoin();
                    }}
                    className="flex items-center text-white bg-gradient-to-r from-theme-primary to-pink-600 hover:from-theme-primary-darker hover:to-pink-700 transition-all px-3 sm:px-4 py-2 rounded-full shadow-lg font-semibold text-sm min-h-[44px]"
                  >
                    <Users size={16} className="sm:mr-2" />
                    <span className="hidden sm:inline">Join this Vybe</span>
                    <span className="sm:hidden">Join</span>
                  </button>
                )}
              </>
            )}

            {!isAuthenticated && (
              <button
                onClick={() => {
                  const joinSection = document.getElementById('join-section');
                  if (joinSection) {
                    joinSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }}
                className="sm:hidden flex items-center text-white bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 transition-all px-4 py-2 rounded-full shadow-lg font-bold text-sm min-h-[44px]"
              >
                <Users size={16} className="mr-1.5" />
                Join (free)
              </button>
            )}

            <button
              onClick={() => setShowReportForm(true)}
              className="flex items-center justify-center text-white bg-theme-cards/80 backdrop-blur-sm hover:bg-theme-cards transition-all w-10 h-10 rounded-full shadow-lg border border-theme-cards"
              title="Report event"
            >
              <Flag size={18} />
            </button>
          </div>
        </div>

        {/* Hero Content */}
        <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-6">
          <div className="container mx-auto max-w-6xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              {event.isFeatured && (
                <div className="inline-flex items-center bg-theme-primary/20 border border-theme-primary/50 text-theme-primary px-3 py-1 rounded-full text-sm font-bold mb-3">
                  <Sparkles size={14} className="mr-1.5" />
                  Featured Vybe
                </div>
              )}

              <h1 className="text-2xl sm:text-4xl md:text-5xl font-sora font-bold text-white mb-3 sm:mb-4 drop-shadow-lg line-clamp-2">
                {event.title}
              </h1>

              <div className="flex flex-wrap gap-2 items-center text-xs sm:text-sm">
                <div className="flex items-center bg-theme-cards/80 backdrop-blur-sm border border-theme-cards px-3 py-1.5 rounded-full">
                  <Calendar size={16} className="mr-2 text-theme-primary" />
                  <span className="text-white text-sm font-medium">{formatDate(event.date)}</span>
                </div>

                <div className="flex items-center bg-theme-cards/80 backdrop-blur-sm border border-theme-cards px-3 py-1.5 rounded-full">
                  <MapPin size={16} className="mr-2 text-theme-primary" />
                  <span className="text-white text-sm font-medium">
                    {isAcceptedMember(event.id, currentUser?.id, event.posterId, applications)
                      ? event.location
                      : getApproximateLocation(event.location)}
                  </span>
                </div>

                <div className="flex items-center bg-theme-cards/80 backdrop-blur-sm border border-theme-cards px-3 py-1.5 rounded-full">
                  <Tag size={16} className="mr-2 text-theme-primary" />
                  <span className="text-white text-sm font-medium">{event.category}</span>
                </div>

                {spotsRemaining !== null && (
                  <div className={`flex items-center backdrop-blur-sm px-3 py-1.5 rounded-full font-semibold text-sm border ${
                    isFull ? 'bg-red-600/80 border-red-500 text-white' :
                    'bg-green-600/80 border-green-500 text-white'
                  }`}>
                    <Users size={16} className="mr-2" />
                    {isFull ? 'Full' : `${spotsRemaining} spots left`}
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto max-w-7xl px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-8">
          {/* Left Column - Main Content */}
          <div className="space-y-6 min-w-0">
            {/* Description Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
              className="bg-theme-cards rounded-2xl shadow-card p-6 "
            >
              <div className="flex items-center mb-4">
                <div className="w-1 h-6 bg-theme-primary rounded-full mr-3"></div>
                <h2 className="text-xl font-sora font-bold text-white">About this Vybe</h2>
              </div>
              <p className="text-theme-text-secondary text-base leading-relaxed whitespace-pre-line">{event.description}</p>
            </motion.div>

            {/* Location Map Card */}
            {event.latitude && event.longitude && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-theme-cards rounded-2xl shadow-card overflow-hidden"
              >
                <div className="p-4 border-b border-theme-cards/50">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <MapPin size={20} className="text-theme-primary mt-1 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        {isAcceptedMember(event.id, currentUser?.id, event.posterId, applications) ? (
                          <>
                            <p className="text-white text-base font-medium leading-tight mb-1.5">{event.location}</p>
                            <p className="text-xs text-green-400">Exact meeting spot shared</p>
                          </>
                        ) : (
                          <>
                            <p className="text-white text-base font-medium leading-tight mb-1.5">
                              Near {getApproximateLocation(event.location)}
                            </p>
                            <p className="text-xs text-theme-text-secondary">Exact address shared after acceptance</p>
                          </>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => setShowMapModal(true)}
                      className="p-2 hover:bg-theme-bg-hover rounded-lg transition-colors flex-shrink-0"
                      title="Expand map"
                    >
                      <Expand size={18} className="text-theme-text-secondary" />
                    </button>
                  </div>
                </div>
                <div className="relative w-full h-64 bg-theme-background cursor-pointer" onClick={() => setShowMapModal(true)}>
                  <MapView
                    events={[event]}
                    selectedEvent={event}
                    userLocation={
                      currentUser?.latitude && currentUser?.longitude
                        ? { lat: currentUser.latitude, lng: currentUser.longitude }
                        : undefined
                    }
                    currentUserId={currentUser?.id}
                    interactive={false}
                    showUserLocation={true}
                    acceptedEventIds={isAcceptedAttendee ? [event.id] : []}
                    className="w-full h-full"
                  />
                </div>
              </motion.div>
            )}

            {/* Preferred Demographics Card */}
            {isAuthenticated && event.preferredDemographics && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-theme-cards rounded-2xl shadow-card p-6 "
              >
                <div className="flex items-center mb-4">
                  <div className="w-1 h-6 bg-theme-primary rounded-full mr-3"></div>
                  <h3 className="text-xl font-sora font-bold text-white">Preferred Demographics</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {event.preferredDemographics.gender && event.preferredDemographics.gender !== 'prefer-not-to-say' && (
                    <div className="flex items-center bg-theme-background/50 p-3 rounded-lg">
                      <div className="w-8 h-8 bg-theme-primary/20 rounded-lg flex items-center justify-center mr-3">
                        <UserIcon size={16} className="text-theme-primary" />
                      </div>
                      <div>
                        <p className="text-xs text-theme-text-secondary font-medium">Gender</p>
                        <p className="text-white font-medium text-sm">
                          {event.preferredDemographics.gender === 'non-binary'
                            ? 'Non-binary/Other'
                            : event.preferredDemographics.gender.charAt(0).toUpperCase() + event.preferredDemographics.gender.slice(1)}
                        </p>
                      </div>
                    </div>
                  )}

                  {(event.preferredDemographics.minAge !== undefined || event.preferredDemographics.maxAge !== undefined) && (
                    <div className="flex items-center bg-theme-background/50 p-3 rounded-lg">
                      <div className="w-8 h-8 bg-theme-primary/20 rounded-lg flex items-center justify-center mr-3">
                        <Clock size={16} className="text-theme-primary" />
                      </div>
                      <div>
                        <p className="text-xs text-theme-text-secondary font-medium">Age Range</p>
                        <p className="text-white font-medium text-sm">
                          {event.preferredDemographics.minAge || 18} - {event.preferredDemographics.maxAge || '65+'}
                        </p>
                      </div>
                    </div>
                  )}

                  {event.preferredDemographics.interests && event.preferredDemographics.interests.length > 0 && (
                    <div className="md:col-span-2 bg-theme-background/50 p-3 rounded-lg">
                      <p className="text-xs font-medium text-theme-text-secondary mb-2">Preferred Interests</p>
                      <div className="flex flex-wrap gap-2">
                        {event.preferredDemographics.interests.map((interest) => (
                          <span
                            key={interest}
                            className="bg-primary-100 text-contrast px-3 py-1 rounded-full text-xs font-medium"
                          >
                            {interest}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {isOwner && (
                  <div className="mt-4 pt-4 border-t border-theme-background/50">
                    <div className="flex items-start bg-theme-background/30 border border-theme-background/50 p-3 rounded-lg">
                      <div className="w-8 h-8 bg-theme-background/50 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                        <Check size={16} className="text-white" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-white text-sm">You started this Vybe</h4>
                        <p className="text-xs text-theme-text-secondary mt-1">
                          Check your notifications for any requests from interested Tribe Members.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            )}

            {/* Organizer Card */}
            {!isOwner && (event.isBotCreated || event.systemHost) && (
              /* Bot-Created Vybe - Show Vybe Tribe Team Info */
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-theme-cards rounded-2xl shadow-card p-6"
              >
                <div className="flex items-center mb-4">
                  <div className="w-1 h-6 bg-theme-primary rounded-full mr-3"></div>
                  <h3 className="text-xl font-sora font-bold text-white">Host</h3>
                </div>

                <div className="flex items-start mb-4">
                  <div className="w-16 h-16 rounded-xl overflow-hidden border-2 border-pink-500 shadow-lg">
                    <img
                      src={`https://ui-avatars.com/api/?name=Vybe+Tribe+Team&background=ec4899&color=FFFFFF&size=64&bold=true`}
                      alt="Vybe Tribe Team"
                      className="w-full h-full object-cover"
                    />
                  </div>

                  <div className="ml-3 flex-1">
                    <h4 className="font-bold text-white text-base">Vybe Tribe Team</h4>
                    <p className="text-xs text-theme-text-secondary leading-relaxed mt-1">
                      Community Vybe — created by the Vybe Tribe Team for the community.
                    </p>
                  </div>
                </div>

                <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                      <Users size={16} className="text-blue-400" />
                    </div>
                    <div>
                      <h5 className="text-sm font-semibold text-white mb-1">Community Vybe</h5>
                      <p className="text-xs text-theme-text-secondary leading-relaxed">
                        Created by the Vybe Tribe Team. No designated host attending.
                        Attendees coordinate together in the group chat.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Regular User-Hosted Vybe Organizer Card */}
            {!isOwner && !(event.isBotCreated || event.systemHost) && poster && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-theme-cards rounded-2xl shadow-card p-6"
              >
                <div className="flex items-center mb-4">
                  <div className="w-1 h-6 bg-theme-primary rounded-full mr-3"></div>
                  <h3 className="text-xl font-sora font-bold text-white">Meet the Organizer</h3>
                </div>

                <div className="flex items-start mb-4">
                  <div className="relative">
                    <div className="w-16 h-16 rounded-xl overflow-hidden border-2 border-theme-cards shadow-lg">
                      {poster.avatarUrl ? (
                        <img
                          src={poster.avatarUrl}
                          alt={poster.username}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-theme-primary flex items-center justify-center text-white text-xl font-bold">
                          {poster.username?.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                    {poster.isVerified && (
                      <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center border-2 border-theme-cards shadow-lg">
                        <Check size={12} className="text-white" />
                      </div>
                    )}
                  </div>

                  <div className="ml-3 flex-1">
                    <h4 className="font-bold text-white text-base">{poster.username}</h4>
                    {poster.gender && (
                      <p className="text-xs text-theme-text-secondary">
                        {poster.gender === 'non-binary' ? 'Non-binary/Other' : poster.gender.charAt(0).toUpperCase() + poster.gender.slice(1)}
                        {poster.age && `, ${poster.age} years`}
                      </p>
                    )}
                  </div>
                </div>

                {poster.bio && (
                  <div className="mb-4">
                    <h5 className="text-sm font-medium text-theme-text-secondary mb-2">About {poster.username}</h5>
                    <p className="text-theme-text-secondary text-sm leading-relaxed p-3 bg-theme-background/30 rounded-lg">
                      {poster.bio}
                    </p>
                  </div>
                )}

                {poster.interests && poster.interests.length > 0 && (
                  <div className="mb-4">
                    <h5 className="text-sm font-medium text-theme-text-secondary mb-2">Interests</h5>
                    <div className="flex flex-wrap gap-2">
                      {poster.interests.map((interest) => (
                        <span
                          key={interest}
                          className="text-xs bg-primary-100 text-contrast px-3 py-1 rounded-full"
                        >
                          {interest}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-around pt-4 border-t border-theme-background/30">
                  <div className="text-center">
                    <div className="text-xl font-bold text-white">{poster.ratingCount || 0}</div>
                    <div className="text-xs text-theme-text-secondary">Reviews</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl font-bold text-white">{(!poster.ratingCount || poster.ratingCount < 3) ? 'new' : poster.rating?.toFixed(1) || 'N/A'}</div>
                    <div className="text-xs text-theme-text-secondary mt-1">Rating</div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Applications List for Owner */}
            {isOwner && applications.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-theme-cards rounded-2xl shadow-card p-6 "
              >
                <ApplicationList
                  applications={applications}
                  event={{ id: event.id, title: event.title, posterId: event.posterId }}
                  onChange={() => {
                    // Reload applications when status changes
                    getApplicationsForEvent(event.id).then(setApplications);
                    onApplied();
                  }}
                />
              </motion.div>
            )}
          </div>

          {/* Right Column - Join */}
          <div className="space-y-6 lg:sticky lg:top-4 lg:self-start">


            {/* Social Share Bar for Post-Event */}
            {userApplication?.status === 'accepted' && new Date(event.date) < new Date() && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.15 }}
              >
                <SocialShareBar
                  shareTitle={`We vybed at ${event.title}`}
                  shareText="Great crew, great energy — join the next one!"
                  shareUrl={`${window.location.origin}/events/${event.id}`}
                />
              </motion.div>
            )}

            {/* Join Card */}
            {isAuthenticated && !isOwner && (
              <motion.div
                id="join-section"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-theme-cards rounded-2xl shadow-card p-4 sm:p-6"
              >
                <div className="flex items-center mb-3 sm:mb-4">
                  <div className="w-1 h-5 sm:h-6 bg-theme-primary rounded-full mr-2 sm:mr-3"></div>
                  <h3 className="text-base sm:text-lg font-sora font-bold text-white">Join this Vybe</h3>
                </div>

                {userApplication && userApplication.status !== 'withdrawn' ? (
                  <>
                    {userApplication.status === 'accepted' ? (
                      <VybeStatusCard
                        status="accepted"
                        isBotVybe={event.isBotCreated || event.systemHost || event.autoAcceptEnabled || false}
                        eventDate={formatDate(event.date)}
                        eventTime={event.time || 'TBD'}
                        onGoToChat={async () => {
                          try {
                            // Ensure thread exists and user is a participant
                            const { data: threadId, error: threadError } = await supabase.rpc(
                              'get_or_create_vybe_thread',
                              { vybe_id_input: event.id, user_id_input: currentUser!.id }
                            );
                            if (threadError) {
                              console.error('Failed to get/create thread:', threadError);
                              throw threadError;
                            }
                            // Navigate to messages page with thread ID
                            navigate(`/messages?thread=${threadId}`);
                          } catch (error) {
                            console.error('Error opening group chat:', error);
                            // Fallback to messages page
                            navigate('/messages');
                          }
                        }}
                        onWithdraw={handleWithdraw}
                        isWithdrawing={isWithdrawing}
                      />
                    ) : userApplication.status === 'rejected' ? (
                      <div className="bg-red-500/10 border border-red-500/20 p-4 rounded-lg text-center">
                        <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 bg-red-500/20">
                          <X size={24} className="text-red-500" />
                        </div>
                        <h4 className="font-bold mb-1 text-red-500">Not This Time</h4>
                        <p className="text-sm text-theme-text-secondary">Not a match this time. Keep exploring other Vybes!</p>
                      </div>
                    ) : userApplication.status === 'pending' ? (
                      <VybeStatusCard
                        status="requested"
                        isBotVybe={event.isBotCreated || event.systemHost || event.autoAcceptEnabled || false}
                        eventDate={formatDate(event.date)}
                        eventTime={event.time || 'TBD'}
                      />
                    ) : null}
                  </>
                ) : (
                  <>
                    {error && (
                      <div className="bg-red-500/10 border border-red-500/20 p-3 rounded-lg mb-3 sm:mb-4 flex items-start">
                        <AlertCircle size={16} className="sm:w-[18px] sm:h-[18px] mr-2 mt-0.5 flex-shrink-0 text-red-500" />
                        <div>
                          <p className="font-semibold text-red-500 text-xs sm:text-sm">Error</p>
                          <p className="text-xs text-theme-text-secondary">{error}</p>
                        </div>
                      </div>
                    )}

                    {/* Show info message for Community Vybes */}
                    {(event.isBotCreated || event.systemHost || event.autoAcceptEnabled) && (
                      <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 sm:p-4 mb-4">
                        <div className="flex items-start">
                          <div className="w-7 h-7 sm:w-8 sm:h-8 bg-blue-500/20 rounded-lg flex items-center justify-center mr-2 sm:mr-3 flex-shrink-0">
                            <Users size={14} className="text-blue-400 sm:w-4 sm:h-4" />
                          </div>
                          <div>
                            <h4 className="font-bold text-white text-xs sm:text-sm mb-0.5 sm:mb-1">Community Vybe</h4>
                            <p className="text-xs text-theme-text-secondary leading-relaxed">
                              One tap to join! No host approval needed. Coordinate with other attendees in the group chat.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    <button
                      onClick={() => {
                        trackJoinVybeClicked(event.id, event.title, 'detail_page', event.isBotCreated || event.systemHost || event.autoAcceptEnabled || false);
                        handleApply();
                      }}
                      disabled={isSubmitting}
                      className="w-full flex items-center justify-center bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 active:from-red-700 active:to-pink-800 text-white px-4 sm:px-6 py-3 sm:py-3.5 rounded-full transition-all disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-sm sm:text-base shadow-lg min-h-[48px]"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-2 border-white border-t-transparent mr-2"></div>
                          <span className="text-sm sm:text-base">
                            {(event.isBotCreated || event.systemHost || event.autoAcceptEnabled) ? 'Joining...' : 'Sending request...'}
                          </span>
                        </>
                      ) : (
                        <>
                          <Send size={16} className="mr-2 sm:w-5 sm:h-5" />
                          <span className="text-sm sm:text-base">
                            {(event.isBotCreated || event.systemHost || event.autoAcceptEnabled) ? 'Join this Vybe' : 'Send Request'}
                          </span>
                        </>
                      )}
                    </button>
                  </>
                )}
              </motion.div>
            )}

            {isOwner && (
              <>
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 }}
                  className="bg-theme-background/30 border border-theme-background/50 rounded-2xl p-6"
                >
                  <div className="flex items-start">
                    <div className="w-10 h-10 bg-theme-background/50 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                      <Check size={20} className="text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-white text-sm mb-1">You created this Vybe</h4>
                      <p className="text-xs text-theme-text-secondary">
                        Check your notifications for requests to join. You can message approved participants directly.
                      </p>
                    </div>
                  </div>
                </motion.div>
              </>
            )}

            {!isAuthenticated && (
              <motion.div
                id="join-section"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-theme-cards rounded-2xl shadow-card p-6 border border-theme-cards text-center space-y-4"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-red-500/20 to-pink-600/20 rounded-full flex items-center justify-center mx-auto">
                  <Users size={32} className="text-theme-primary" />
                </div>

                {/* Primary CTA */}
                <button
                  onClick={() => navigate('/login?signup=true')}
                  className="block w-full bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white px-6 py-3 rounded-lg font-bold transition-all shadow-lg hover:shadow-xl min-h-[44px] flex items-center justify-center"
                >
                  Join this Vybe (free)
                </button>

                {/* Optional secondary CTA */}
                <button
                  onClick={() => navigate('/vybes')}
                  className="text-theme-text-secondary hover:text-white text-sm underline underline-offset-2 transition-colors inline-block"
                >
                  Browse more Vybes
                </button>
              </motion.div>
            )}

            {/* Who's Going List - Show for all users when there are accepted participants */}
            {totalAccepted > 0 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
              >
                <WhosGoingList eventId={event.id} totalAccepted={totalAccepted} hostId={event.posterId} />
              </motion.div>
            )}
          </div>
        </div>
      </div>

      {/* Sticky Action Bar */}
      <AnimatePresence>
        {showStickyBar && isAuthenticated && !isOwner && (!userApplication || userApplication.status === 'withdrawn') && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            className="fixed bottom-0 left-0 right-0 bg-theme-cards border-t border-theme-cards shadow-card z-50 hidden sm:block"
          >
            <div className="container mx-auto max-w-6xl px-4 py-3">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-white text-sm">{event.title}</h3>
                  <p className="text-xs text-theme-text-secondary">{formatDate(event.date)}</p>
                </div>
                <button
                  onClick={() => {
                    trackJoinVybeClicked(event.id, event.title, 'sticky_bar', event.isBotCreated || event.systemHost || event.autoAcceptEnabled || false);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white px-6 py-2 rounded-full transition-all font-semibold text-sm shadow-lg"
                >
                  Join this Vybe
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modals */}
      <ReportForm isOpen={showReportForm} onClose={() => setShowReportForm(false)} reportType="content" />
      {showEditModal && (
        <EditEventModal isOpen={showEditModal} onClose={() => setShowEditModal(false)} onEventUpdated={onApplied} event={event} />
      )}
      <SubscriptionModal isOpen={showSubscriptionModal} onClose={() => setShowSubscriptionModal(false)} requiredFeature="Joining Vybes" />
      <VybeCounterModal
        isOpen={showVybeCounterModal}
        onClose={() => setShowVybeCounterModal(false)}
        onUpgrade={() => {
          setShowVybeCounterModal(false);
          navigate('/pricing');
        }}
      />
      <PostEventRatingPrompt
        isOpen={showRatingModal}
        onClose={() => setShowRatingModal(false)}
        eventId={event.id}
        targets={ratingTargets}
        currentUserId={currentUser?.id || ''}
        isBotCreated={event.isBotCreated || event.systemHost || false}
      />
      <ApplicationConfirmationModal
        isOpen={showApplicationConfirmation}
        onClose={() => setShowApplicationConfirmation(false)}
        eventTitle={event.title}
      />
      <TokenTopUpModal
        isOpen={showTokenModal}
        onClose={() => setShowTokenModal(false)}
        reason={limitErrorMessage}
      />

      {/* Map Modal */}
      <MapModal
        isOpen={showMapModal}
        onClose={() => setShowMapModal(false)}
        event={event}
        userLocation={
          currentUser?.latitude && currentUser?.longitude
            ? { lat: currentUser.latitude, lng: currentUser.longitude }
            : undefined
        }
        currentUserId={currentUser?.id}
        acceptedEventIds={isAcceptedAttendee ? [event.id] : []}
      />

      {/* Share Modal */}
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        title={event.title}
        shareUrl={`${import.meta.env.VITE_APP_URL || 'https://vybetribe.app'}/events/${event.id}`}
      />

      {/* Push Notification Prompt */}
      {showPushPrompt && (
        <PushNotificationPrompt trigger={isOwner ? "hosted" : "joined"} />
      )}
    </div>
  );
};

export default React.memo(EventDetail);
